import { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "layouts/ecommerce/products/edit-product/components/FormField";

import { getAllEventTitleApi } from "api/events";
import { getAllProducersApi } from "api/superAdmin";

import { AuthContext } from "context/authContext";

function FormFields({ initialData, formValues: formValuesProp }) {
  const { user, userRole } = useContext(AuthContext);
  console.log("User Role from Context:", userRole);

  const isProducer = userRole?.toLowerCase() === "producer";

  const [formDataValues, setFormDataValues] = useState({
    eventID: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    pseudo: "",
    password: "",
    confirmPass: "",
    fromProduced: null,
  });

  const [eventOptions, setEventOptions] = useState([]);
  const [producerOptions, setProducerOptions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedProducer, setSelectedProducer] = useState(null);

  useEffect(() => {
    const fetchAutocompleteOptions = async () => {
      try {
        const eventResponse = await getAllEventTitleApi();
        const events = Array.isArray(eventResponse?.items)
          ? eventResponse.items
          : Array.isArray(eventResponse)
          ? eventResponse
          : [];
        const eventOptionsArray = events.map((event) => ({
          label: event.eventTitle,
          id: event._id,
        }));
        setEventOptions(eventOptionsArray);

        if (!isProducer) {
          const producerResponse = await getAllProducersApi("", "", "", "", "Tous", 1, 10000);
          const producers = Array.isArray(producerResponse?.producers)
            ? producerResponse.producers
            : Array.isArray(producerResponse)
            ? producerResponse
            : [];
          const producerOptionsArray = producers.map((producer) => ({
            label: producer.email,
            id: producer._id,
          }));
          setProducerOptions(producerOptionsArray);
        }
      } catch (err) {
        console.error("Error fetching autocomplete options:", err);
      }
    };

    if (user) {
      fetchAutocompleteOptions();
    }
  }, [isProducer, user]);

  useEffect(() => {
    if (initialData && user) {
      const correctFromProducedId = isProducer ? user._id : initialData.fromProduced;

      const updatedFormData = {
        ...initialData,
        eventID: initialData.eventID ?? null,
        fromProduced: correctFromProducedId ?? null,
      };

      setFormDataValues(updatedFormData);
      formValuesProp(updatedFormData);

      if (initialData.eventID && eventOptions.length > 0) {
        const event = eventOptions.find((opt) => opt.id === initialData.eventID);
        setSelectedEvent(event || null);
      }

      if (isProducer) {
        setSelectedProducer({ id: user._id, label: user.email });
      } else if (initialData.fromProduced && producerOptions.length > 0) {
        const producer = producerOptions.find((opt) => opt.id === initialData.fromProduced);
        setSelectedProducer(producer || null);
      }
    }
  }, [initialData, eventOptions, producerOptions, isProducer, user, formValuesProp]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormDataValues = {
      ...formDataValues,
      [name]: value,
    };
    if (name === "password" && !value) {
      delete newFormDataValues.password;
    }
    if (name === "confirmPass" && !value) {
      delete newFormDataValues.confirmPass;
    }
    setFormDataValues(newFormDataValues);
    formValuesProp(newFormDataValues);
  };

  const handleAutoCompleteEventChange = (event, newValue) => {
    const newFormDataValues = {
      ...formDataValues,
      eventID: newValue ? newValue.id : null,
    };
    setFormDataValues(newFormDataValues);
    setSelectedEvent(newValue);
    formValuesProp(newFormDataValues);
  };

  const handleAutoCompleteProducerChange = (event, newValue) => {
    const newFormDataValues = {
      ...formDataValues,
      fromProduced: newValue ? newValue.id : null,
    };
    setFormDataValues(newFormDataValues);
    setSelectedProducer(newValue);
    formValuesProp(newFormDataValues);
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h5">Informations de Revendeur</MDTypography>
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={eventOptions}
                getOptionLabel={(option) => option?.label || ""}
                value={selectedEvent}
                onChange={handleAutoCompleteEventChange}
                renderInput={(params) => (
                  <FormField {...params} label="Événement Associé" required />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              {isProducer ? (
                <FormField label="Produit Par" value={user?.email || ""} disabled />
              ) : (
                <Autocomplete
                  options={producerOptions}
                  getOptionLabel={(option) => option?.label || ""}
                  value={selectedProducer}
                  onChange={handleAutoCompleteProducerChange}
                  renderInput={(params) => <FormField {...params} label="Produit Par" />}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="text"
                label="Nom"
                name="firstName"
                value={formDataValues.firstName || ""}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="text"
                label="Prénom"
                name="lastName"
                value={formDataValues.lastName || ""}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="email"
                label="Adresse e-mail"
                name="email"
                value={formDataValues.email || ""}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="text"
                label="N° de téléphone"
                name="phone"
                value={formDataValues.phone || ""}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="text"
                label="Pseudo"
                name="pseudo"
                value={formDataValues.pseudo || ""}
                onChange={handleInputChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="password"
                label="Mot de passe"
                name="password"
                value={formDataValues.password || ""}
                onChange={handleInputChange}
                required={!initialData}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormField
                type="password"
                label="Confirmer le Mot de passe"
                name="confirmPass"
                value={formDataValues.confirmPass || ""}
                onChange={handleInputChange}
                required={!initialData}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

FormFields.propTypes = {
  initialData: PropTypes.object,
  formValues: PropTypes.func.isRequired,
};

export default FormFields;
